import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ApiRequest from "../../../helper/ApiRequest";
import { ApiEndpoints } from "../../../helper/ApiEndpoints";
import { BaseUrl } from "../../../helper/BaseUrl";
import CustomSelect from "../../../component/CustomSelect";
import CustomImageUpload from "../../../component/CustomImageUpload";
import DynamicCKEditor from "../../../component/DynamicCkEditor";
import { toast } from "react-toastify";

const categoryListEndpoint = ApiEndpoints.tmsIntegrationCategory.categoryList;

const sectionImageKey = (id) => (id === 1 ? "section_image" : `section_image${id}`);
const sectionMediaTypeKey = (id) => (id === 1 ? "section_media_type" : `section_media_type${id}`);
const videoLinkKey = (id) => (id === 1 ? "video_link" : `video_link${id}`);
const sectionIdsForCount = (count) => Array.from({ length: count }, (_, index) => index + 1);

const defaultData = {
  category_id: "",
  name: "",
  slug: "",
  short_description: "",
  banner_short_description: "",
  video_link: "",
  video_title: "",
  video_subtitle: "",
  last_video_url: "",
  logo: null,
  section_image: null,
  section_media_type: "image",
  heading1: "",
  description1: "",
};

export default function TmsIntegrationForm({ formId, initialData, onSubmit }) {
  const [categories, setCategories] = useState([]);
  const [sectionIds, setSectionIds] = useState([1]);
  const [postData, setPostData] = useState(defaultData);
  const [previews, setPreviews] = useState({ logo: null, section_image: null });

  const compactSections = (sourceData, sourcePreviews, ids) => {
    const nextData = { ...sourceData };
    const nextPreviews = { ...sourcePreviews };

    for (let i = 1; i <= 10; i++) {
      delete nextData[`heading${i}`];
      delete nextData[`description${i}`];
      delete nextData[sectionImageKey(i)];
      delete nextData[sectionMediaTypeKey(i)];
      delete nextData[videoLinkKey(i)];
      delete nextPreviews[sectionImageKey(i)];
    }

    ids.forEach((oldId, index) => {
      const newId = index + 1;
      nextData[`heading${newId}`] = sourceData[`heading${oldId}`] || "";
      nextData[`description${newId}`] = sourceData[`description${oldId}`] || "";
      nextData[sectionImageKey(newId)] = sourceData[sectionImageKey(oldId)] || null;
      nextData[sectionMediaTypeKey(newId)] = sourceData[sectionMediaTypeKey(oldId)] || "image";
      nextData[videoLinkKey(newId)] = sourceData[videoLinkKey(oldId)] || "";
      nextPreviews[sectionImageKey(newId)] = sourcePreviews[sectionImageKey(oldId)] || null;
    });

    return { data: nextData, previews: nextPreviews, ids: sectionIdsForCount(ids.length || 1) };
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await ApiRequest("GET", categoryListEndpoint);
        setCategories(response?.tms_integration_categories || []);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!initialData) return;

    const imagePreviews = {
      logo: initialData.logo ? BaseUrl + initialData.logo : null,
    };
    const sections = [];

    for (let i = 1; i <= 10; i++) {
      const imageKey = sectionImageKey(i);
      const linkKey = videoLinkKey(i);

      if (initialData[`heading${i}`] || initialData[`description${i}`] || initialData[imageKey] || initialData[linkKey] || initialData[sectionMediaTypeKey(i)] === "video") {
        sections.push(i);
      }
      if (initialData[imageKey]) {
        imagePreviews[imageKey] = BaseUrl + initialData[imageKey];
      }
    }

    const compactedSections = compactSections({ ...defaultData, ...initialData }, imagePreviews, sections.length ? sections : [1]);

    setPostData(compactedSections.data);
    setPreviews(compactedSections.previews);
    setSectionIds(compactedSections.ids);
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    const mediaTypeMatch = name.match(/^section_media_type(\d*)$/);

    if (mediaTypeMatch) {
      const sectionId = mediaTypeMatch[1] ? Number(mediaTypeMatch[1]) : 1;
      const imageKey = sectionImageKey(sectionId);
      const linkKey = videoLinkKey(sectionId);

      setPostData((prev) => ({
        ...prev,
        [name]: value,
        ...(value === "image" ? { [linkKey]: "" } : { [imageKey]: null }),
      }));

      if (value === "video") {
        setPreviews((prev) => ({ ...prev, [imageKey]: null }));
      }
      return;
    }

    if (files && files[0]) {
      const file = files[0];
      setPreviews((prev) => ({ ...prev, [name]: URL.createObjectURL(file) }));
      setPostData((prev) => ({ ...prev, [name]: file }));
      return;
    }

    setPostData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditorChange = (key, data) => {
    setPostData((prevState) => ({ ...prevState, [key]: data }));
  };

  const handleAddMoreSection = (e) => {
    e.preventDefault();
    const nextId = sectionIds.length + 1;
    if (nextId > 10) {
      toast.warning("You can only add up to 10 sections.");
      return;
    }

    setSectionIds((prev) => [...prev, nextId]);
    setPostData((prev) => ({
      ...prev,
      [`heading${nextId}`]: "",
      [`description${nextId}`]: "",
      [sectionImageKey(nextId)]: null,
      [sectionMediaTypeKey(nextId)]: "image",
      [videoLinkKey(nextId)]: "",
    }));
  };

  const handleRemoveSection = (id) => {
    if (id === 1) return;

    const remainingIds = sectionIds.filter((num) => num !== id);
    const compacted = compactSections(postData, previews, remainingIds);

    setSectionIds(compacted.ids);
    setPostData(compacted.data);
    setPreviews(compacted.previews);
  };

  const submitForm = (e) => {
    e.preventDefault();
    const requiredFields = [
      "category_id",
      "name",
      "slug",
      "short_description",
      "banner_short_description",
      "logo",
      "heading1",
      "description1",
    ];

    const compactedSections = compactSections(postData, previews, sectionIds);
    const compactedData = compactedSections.data;

    for (const key of requiredFields) {
      if (!compactedData[key]) {
        toast.error(`The field "${key}" is mandatory.`);
        return;
      }
    }

    for (const id of compactedSections.ids) {
      const mediaType = compactedData[sectionMediaTypeKey(id)] || "image";
      const hasContent = id === 1 || compactedData[`heading${id}`] || compactedData[`description${id}`];

      if (mediaType === "image" && id === 1 && !compactedData[sectionImageKey(id)]) {
        toast.error('The field "section_image" is mandatory.');
        return;
      }

      if (mediaType === "video" && hasContent && !compactedData[videoLinkKey(id)]) {
        toast.error(`The field "${videoLinkKey(id)}" is mandatory.`);
        return;
      }
    }

    const formData = new FormData();
    Object.entries(compactedData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) formData.append(key, value);
    });
    formData.append("_section_count", compactedSections.ids.length);
    onSubmit(formData);
  };

  return (
    <form id={formId} onSubmit={submitForm}>
      <div className="add-content-wrap">
        <div className="row">
          <div className="col-lg-6">
            <div className="add-page-inputs">
              <div className="label-wrap-add"><label>Name<small>*</small></label></div>
              <input value={postData.name || ""} onChange={handleChange} name="name" type="text" className="form-control" placeholder="Enter integration name" />
            </div>
          </div>
          <div className="col-lg-6">
            <div className="add-page-inputs">
              <div className="label-wrap-add"><label>Slug<small>*</small></label></div>
              <input value={postData.slug || ""} onChange={handleChange} name="slug" type="text" className="form-control" placeholder="enter-slug" />
            </div>
          </div>
          <div className="col-lg-12">
            <div className="add-page-inputs">
              <div className="label-wrap-add"><label>Category<small>*</small></label></div>
              <CustomSelect defaultValue={postData.category_id} onChange={handleChange} name="category_id" options={categories} placeholder="Select Category" />
            </div>
          </div>

          <div className="col-lg-12">
            <div className="add-page-inputs input-add-image">
              <div className="label-wrap-add"><label>Logo Image(64 × 64 px)<small>*</small></label></div>
              <CustomImageUpload name="logo" acceptedFormats={[".webp"]} onImageChange={handleChange} imagePreview={previews.logo} />
            </div>
          </div>


          <div className="col-lg-6">
            <div className="add-page-inputs">
              <div className="label-wrap-add"><label>Listing Description<small>*</small></label></div>
              <textarea value={postData.short_description || ""} onChange={handleChange} name="short_description" className="form-control" placeholder="Short text for listing card" rows="4" />
            </div>
          </div>
          <div className="col-lg-6">
            <div className="add-page-inputs">
              <div className="label-wrap-add"><label>Banner Short Description<small>*</small></label></div>
              <textarea value={postData.banner_short_description || ""} onChange={handleChange} name="banner_short_description" className="form-control" placeholder="Short text for detail page banner" rows="4" />
            </div>
          </div>
        </div>
      </div>

      <div className="add-content-wrap">
        <div className="row">
          {sectionIds.map((id) => {
            const imageKey = sectionImageKey(id);
            const mediaTypeKey = sectionMediaTypeKey(id);
            const linkKey = videoLinkKey(id);
            const mediaType = postData[mediaTypeKey] || "image";
            return (
              <div key={id} className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add mb-2 d-flex justify-content-between align-items-center">
                    <label>Heading {id}{id === 1 && <small>*</small>}</label>
                    {id !== 1 && <button type="button" className="remove-btn" onClick={() => handleRemoveSection(id)}>Remove</button>}
                  </div>
                  <input onChange={handleChange} name={`heading${id}`} type="text" className="form-control" placeholder="Enter heading" value={postData[`heading${id}`] || ""} />
                </div>
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Description {id}{id === 1 && <small>*</small>}</label></div>
                  <DynamicCKEditor data={postData[`description${id}`] || ""} onChange={(event, editor) => handleEditorChange(`description${id}`, editor.getData())} />
                </div>
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Media Type {id}{id === 1 && <small>*</small>}</label></div>
                  <select name={mediaTypeKey} value={mediaType} onChange={handleChange} className="form-control">
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                {mediaType === "image" ? (
                  <div className="add-page-inputs input-add-image">
                    <div className="label-wrap-add"><label>Detail Image {id}{id === 1 && <small>*</small>}</label></div>
                    <CustomImageUpload name={imageKey} acceptedFormats={[".webp"]} onImageChange={handleChange} imagePreview={previews[imageKey]} />
                  </div>
                ) : (
                  <div className="add-page-inputs">
                    <div className="label-wrap-add"><label>Video Link {id}{id === 1 && <small>*</small>}</label></div>
                    <input value={postData[linkKey] || ""} onChange={handleChange} name={linkKey} type="text" className="form-control" placeholder="Paste video URL" />
                  </div>
                )}
              </div>
            );
          })}
          <div className="col-lg-12">
            <div className="add-page-inputs">
              <Link className="addmore-images-client" onClick={handleAddMoreSection}>Add More Content +</Link>
            </div>
          </div>
        </div>
      </div>
      <div className="add-content-wrap">
        <div className="row">
          <div className="col-lg-6">
            <div className="add-page-inputs">
              <div className="label-wrap-add"><label>Video Title</label></div>
              <input value={postData.video_title || ""} onChange={handleChange} name="video_title" type="text" className="form-control" placeholder="See It In Action" />
            </div>
          </div>
          <div className="col-lg-6">
            <div className="add-page-inputs">
              <div className="label-wrap-add"><label>Video Subtitle</label></div>
              <input value={postData.video_subtitle || ""} onChange={handleChange} name="video_subtitle" type="text" className="form-control" placeholder="Short video subtitle" />
            </div>
          </div>
          <div className="col-lg-12">
            <div className="add-page-inputs">
              <div className="label-wrap-add"><label>Last Video URL</label></div>
              <input value={postData.last_video_url || ""} onChange={handleChange} name="last_video_url" type="text" className="form-control" placeholder="Paste last video URL" />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
